import { Bus } from '@tiw/event-bus';
import { WSClient, type WSConnectionState } from '@tiw/data-streams';
import { CHANNELS, type EventMap } from '@tiw/shared-types';

/**
 * Process-wide WebSocket client + shared event Bus.
 *
 * The WSClient lives in @tiw/data-streams and owns frame parsing,
 * heartbeat, and reconnect bookkeeping. It fans incoming envelopes into the
 * shared Bus, which any component or hook can subscribe to.
 *
 * See packages/data-streams, packages/event-bus and docs/API_CONTRACTS.md.
 */

// In production, VITE_WS_URL points at the Worker (wss://api.<domain>). In dev
// it's unset, so we use the same origin and let the Vite proxy forward /ws to
// `wrangler dev`. See vite.config.ts and docs/DEPLOYMENT.md.
const WS_ENV = (import.meta.env.VITE_WS_URL ?? '').replace(/\/+$/, '');
const WS_URL =
  WS_ENV !== ''
    ? WS_ENV
    : typeof location !== 'undefined'
      ? `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`
      : 'ws://127.0.0.1:5173';
const STREAM_PATH = '/ws';

export const bus = new Bus<EventMap>();

const stateListeners = new Set<(s: WSConnectionState) => void>();

export const wsClient = new WSClient({
  url: `${WS_URL}${STREAM_PATH}`,
  channels: Object.values(CHANNELS),
  bus,
  heartbeatMs: 15_000,
  onStateChange: (s) => {
    for (const listener of stateListeners) listener(s);
  },
});

export function subscribeWsState(listener: (s: WSConnectionState) => void): () => void {
  stateListeners.add(listener);
  return () => {
    stateListeners.delete(listener);
  };
}

export type { WSClient, WSConnectionState } from '@tiw/data-streams';
